"use client"

import { useEffect, useState, useRef } from "react"
import type { UserAccount } from "@/components/user-edit-dialog"
import { CaretDown as ChevronDown, Plus, FloppyDisk as Save, X } from "@phosphor-icons/react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button, Input } from "animal-island-ui"
import { ActiveTimeModeSelect } from "@/components/active-time-mode-select"
import { ConfigSwitch } from "@/components/config-switch"
import { EditNumberStepper } from "@/components/edit-number-stepper"
import { FightPlanEditor } from "@/components/fight-plan-editor"
import { initializeFightPlan, validateFightPlan, type FightItem } from "@/lib/fight-plan"
import { useToast } from "@/hooks/use-toast"

interface NewUserAccount {
  name: string
  account: string
  password?: string
  freeze: number
  server: number
  taskType: string
  refresh: number
  agent: string | null
  expireTime: string
  config: Record<string, unknown>
  active: Record<string, { enable: boolean; detail?: string[] }>
  notice: Record<string, never>
  days: number
}

interface UserAddDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (user: Partial<UserAccount>) => Promise<boolean>
}

const dayNames: Record<string, string> = {
  monday: "周一", tuesday: "周二", wednesday: "周三", thursday: "周四",
  friday: "周五", saturday: "周六", sunday: "周日",
}

const stageOptions = [
  { value: "jm", label: "剿灭" }, { value: "ce", label: "龙门币" },
  { value: "ls", label: "经验卡" }, { value: "ap", label: "红票" },
  { value: "ca", label: "技能书" }, { value: "1-7", label: "1-7" },
  { value: "hd", label: "活动材料关" }, { value: "pr2", label: "大芯片任意" },
  { value: "pr1", label: "小芯片任意" },
]

function createDefaultForm(): NewUserAccount {
  return {
    name: "", account: "", password: "", freeze: 0, server: 0, taskType: "daily", refresh: 0,
    agent: null, expireTime: "", days: 30, notice: {},
    config: {
      daily: {
        fight: initializeFightPlan(), sanity: { drug: 1, stone: 0 },
        mail: true, friend: true, credit: true, task: true, activity: true, fight_enable: true,
        offer: { enable: true, car: false, star4: true, star5: false, star6: false, other: false },
        infrastructure: { harvest: true, shift: true, acceleration: true, communication: true, deputy: true },
      },
    },
    active: Object.fromEntries(Object.keys(dayNames).map(day => [day, { enable: true }])),
  }
}

export function UserAddDialog({ open, onOpenChange, onSave }: UserAddDialogProps) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const savingRef = useRef(false)
  const [addForm, setAddForm] = useState<NewUserAccount>(createDefaultForm)
  const [fightHelpOpen, setFightHelpOpen] = useState(false)

  useEffect(() => {
    if (open) {
      setAddForm(createDefaultForm())
      setFightHelpOpen(false)
    }
  }, [open])

  const updateConfig = (path: string[], value: unknown) => {
    setAddForm(previous => {
      const config = structuredClone(previous.config) as Record<string, any>
      let target = config
      for (const key of path.slice(0, -1)) target = target[key] ??= {}
      target[path.at(-1)!] = value
      return { ...previous, config }
    })
  }

  const getConfigValue = <T,>(path: string[], fallback: T): T => {
    let target: any = addForm.config
    for (const key of path) {
      if (!target || typeof target !== "object" || !(key in target)) return fallback
      target = target[key]
    }
    return target as T
  }

  const updateDay = (day: string, next: { enable: boolean; detail?: string[] }) => {
    setAddForm(previous => ({ ...previous, active: { ...previous.active, [day]: next } }))
  }

  const handleSave = async () => {
    if (savingRef.current) return
    if (!addForm.name.trim() || !addForm.account.trim() || !addForm.password?.trim()) {
      toast({ variant: "destructive", title: "请补全必填项", description: "用户名、账号和密码不能为空。" })
      return
    }
    const fightError = validateFightPlan(getConfigValue<FightItem[]>(["daily", "fight"], []))
    if (fightError) {
      toast({ variant: "destructive", title: "作战配置有误", description: fightError })
      return
    }
    savingRef.current = true
    setLoading(true)
    try {
      const days = Math.max(1, addForm.days || 1)
      const expireTime = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
      const payload = structuredClone({ ...addForm, name: addForm.name.trim(), account: addForm.account.trim(), expireTime, days })
      if (await onSave(payload) === true) onOpenChange(false)
    } catch (error) {
      toast({ variant: "destructive", title: "添加失败", description: error instanceof Error ? error.message : "请稍后重试，草稿已保留。" })
    } finally {
      savingRef.current = false
      setLoading(false)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!savingRef.current) onOpenChange(nextOpen)
  }

  return <Dialog open={open} onOpenChange={handleOpenChange}>
    <DialogContent className="admin-user-edit admin-user-add max-w-6xl bg-cream-50">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-leaf-900"><Plus className="h-5 w-5" />新增用户</DialogTitle>
        <DialogDescription className="text-leaf-700">先填写账号，再按需展开任务和上号时间。</DialogDescription>
      </DialogHeader>

      <Tabs defaultValue="basic" className="admin-user-edit__tabs w-full">
        <TabsList className="grid w-full grid-cols-2 gap-1 bg-cream-100 border-2 border-leaf-500 p-2 rounded-2xl h-auto">
          <TabsTrigger value="basic" className="data-[state=active]:bg-leaf-500 data-[state=active]:text-white text-leaf-900 font-semibold rounded-xl px-2 py-1.5">基本信息</TabsTrigger>
          <TabsTrigger value="config" className="data-[state=active]:bg-leaf-500 data-[state=active]:text-white text-leaf-900 font-semibold rounded-xl px-2 py-1.5">任务与时间</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <div className="admin-user-edit__basic grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><label htmlFor="add-name">用户名</label><Input id="add-name" value={addForm.name} onChange={event => setAddForm({ ...addForm, name: event.target.value })} required /></div>
            <div className="space-y-2"><label htmlFor="add-account">账号</label><Input id="add-account" value={addForm.account} onChange={event => setAddForm({ ...addForm, account: event.target.value })} required /></div>
            <div className="space-y-2"><label htmlFor="password">密码</label><Input id="password" type="text" value={addForm.password} onChange={event => setAddForm({ ...addForm, password: event.target.value })} required /></div>
            <div className="space-y-2"><label>任务类型</label><Select value={addForm.taskType} onValueChange={taskType => setAddForm({ ...addForm, taskType })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">日常任务</SelectItem><SelectItem value="luxury">豪华套餐</SelectItem><SelectItem value="goods">商品任务</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><label>服务器</label><Select value={String(addForm.server)} onValueChange={server => setAddForm({ ...addForm, server: Number(server) })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="0">官服</SelectItem><SelectItem value="1">B服</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><label htmlFor="add-refresh">刷新次数</label><EditNumberStepper id="add-refresh" value={addForm.refresh} max={99} onValueChange={refresh => setAddForm({ ...addForm, refresh })} /></div>
            <div className="space-y-2"><label htmlFor="add-days">有效天数</label><EditNumberStepper id="add-days" value={addForm.days} min={1} max={3650} onValueChange={days => setAddForm({ ...addForm, days })} /><p className="text-xs">创建后从今天开始计算。</p></div>
            <div className="space-y-2"><label htmlFor="add-agent">代理</label><Input id="add-agent" value={addForm.agent ?? ""} placeholder="可选" onChange={event => setAddForm({ ...addForm, agent: event.target.value || null })} /></div>
          </div>
          <label className="flex min-h-11 items-center justify-between gap-3"><span>冻结账号</span><ConfigSwitch checked={Boolean(addForm.freeze)} onChange={checked => setAddForm({ ...addForm, freeze: checked ? 1 : 0 })} /></label>
        </TabsContent>

        <TabsContent value="config" className="space-y-3">
          <p className="text-sm">按需展开；未修改的选项会使用日常任务默认配置。</p>
          <details className="config-section"><summary>基础配置<ChevronDown size={17} aria-hidden="true" /></summary><div className="config-body"><div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[
            ["mail", "邮件领取"], ["friend", "好友访问"], ["credit", "信用商店"], ["task", "任务领取"], ["activity", "限时活动"],
          ].map(([key, label]) => <label className="flex min-h-11 items-center justify-between gap-2" key={key}><span>{label}</span><ConfigSwitch checked={getConfigValue(["daily", key], false)} onChange={checked => updateConfig(["daily", key], checked)} /></label>)}</div></div></details>
          <details className="config-section"><summary><span className="edit-fight-heading">作战配置<button type="button" className="edit-fight-help" aria-label="作战配置说明" onClick={event => { event.preventDefault(); event.stopPropagation(); setFightHelpOpen(true) }}>说明</button></span><ChevronDown size={17} aria-hidden="true" /></summary><div className="config-body"><label className="edit-time-heading"><span>启用作战</span><ConfigSwitch checked={getConfigValue(["daily", "fight_enable"], true)} onChange={checked => updateConfig(["daily", "fight_enable"], checked)} /></label><FightPlanEditor hideToolbar helpOpen={fightHelpOpen} onHelpOpenChange={setFightHelpOpen} value={getConfigValue<FightItem[]>(["daily", "fight"], [])} options={stageOptions} onChange={fight => updateConfig(["daily", "fight"], fight)} /><div className="grid grid-cols-2 gap-4"><div><label>吃药次数</label><EditNumberStepper value={getConfigValue(["daily", "sanity", "drug"], 0)} max={99} onValueChange={drug => updateConfig(["daily", "sanity", "drug"], drug)} /></div><div><label>碎石次数</label><EditNumberStepper value={getConfigValue(["daily", "sanity", "stone"], 0)} max={99} onValueChange={stone => updateConfig(["daily", "sanity", "stone"], stone)} /></div></div></div></details>
          <details className="config-section"><summary>上号时间设置<ChevronDown size={17} aria-hidden="true" /></summary><div className="config-body"><p className="text-xs">默认全天允许；自定义时段仅在需要限制上号时填写。</p><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{Object.entries(dayNames).map(([day, label]) => { const current = addForm.active[day] ?? { enable: true }; const detail = current.detail ?? []; const mode = !current.enable ? "none" : detail.length ? "custom" : "all"; return <div key={day} className="edit-time-day p-3 border rounded-lg space-y-2"><div className="edit-time-heading"><strong>{label}</strong><ActiveTimeModeSelect day={label} value={mode} onChange={next => updateDay(day, { enable: next !== "none", detail: next === "custom" ? (detail.length ? detail : ["18:00-20:00"]) : [] })} /></div>{current.enable && detail.map((slot, index) => { const [start, end] = slot.split("-"); return <div key={index} className="edit-time-slot"><Input type="time" value={start ?? "08:00"} onChange={event => updateDay(day, { ...current, detail: detail.map((value, itemIndex) => itemIndex === index ? `${event.target.value}-${end ?? "23:00"}` : value) })} /><span>至</span><Input type="time" value={end ?? "23:00"} onChange={event => updateDay(day, { ...current, detail: detail.map((value, itemIndex) => itemIndex === index ? `${start ?? "08:00"}-${event.target.value}` : value) })} /><button type="button" aria-label={`删除${label}时段`} onClick={() => updateDay(day, { ...current, detail: detail.filter((_, itemIndex) => itemIndex !== index) })}>×</button></div> })}</div> })}</div></div></details>
        </TabsContent>
      </Tabs>

      <div className="admin-user-edit__footer flex justify-end gap-2 pt-4">
        <Button onClick={() => handleOpenChange(false)} disabled={loading}><X className="mr-2 h-4 w-4" />取消</Button>
        <Button onClick={handleSave} disabled={loading}><Save className="mr-2 h-4 w-4" />{loading ? "添加中..." : "添加用户"}</Button>
      </div>
    </DialogContent>
  </Dialog>
}
